import AsyncStorage from '@react-native-async-storage/async-storage';

const SYNCED_RANGES_KEY = '__synced_session_date_ranges';

export interface DateRange {
  startDate: number; // Unix timestamp
  endDate: number; // Unix timestamp
}

/**
 * Get all synced session date ranges from storage
 */
export const getSyncedDateRanges = async (): Promise<DateRange[]> => {
  try {
    const stored = await AsyncStorage.getItem(SYNCED_RANGES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    console.error('Error reading synced date ranges:', e);
    return [];
  }
};

/**
 * Add a new synced date range to storage
 */
export const addSyncedDateRange = async (
  startDate: number,
  endDate: number,
): Promise<void> => {
  try {
    const ranges = await getSyncedDateRanges();
    const newRange = {startDate, endDate};
    const mergedRanges = mergeRanges([...ranges, newRange]);
    await AsyncStorage.setItem(SYNCED_RANGES_KEY, JSON.stringify(mergedRanges));
  } catch (e) {
    console.error('Error adding synced date range:', e);
  }
};

/**
 * Check if a given date range has been fully synced
 */
export const isDateRangeSynced = async (
  startDate: number,
  endDate: number,
): Promise<boolean> => {
  try {
    const ranges = await getSyncedDateRanges();
    // Check if the requested range is covered by any synced range
    return ranges.some(
      range => range.startDate <= startDate && range.endDate >= endDate,
    );
  } catch (e) {
    console.error('Error checking if date range is synced:', e);
    return false;
  }
};

/**
 * Clear all synced date ranges (useful for logout or data reset)
 */
export const clearSyncedDateRanges = async (): Promise<void> => {
  try {
    await AsyncStorage.removeItem(SYNCED_RANGES_KEY);
  } catch (e) {
    console.error('Error clearing synced date ranges:', e);
  }
};

/**
 * Get missing date ranges that need to be synced
 */
export const getMissingDateRanges = async (
  requestedStart: number,
  requestedEnd: number,
): Promise<DateRange[]> => {
  const syncedRanges = await getSyncedDateRanges();

  if (syncedRanges.length === 0) {
    return [{startDate: requestedStart, endDate: requestedEnd}];
  }

  const sorted = [...syncedRanges].sort((a, b) => a.startDate - b.startDate);
  const missing: DateRange[] = [];
  let currentStart = requestedStart;

  for (const range of sorted) {
    // If there's a gap before this range, add it
    if (currentStart < range.startDate && currentStart < requestedEnd) {
      missing.push({
        startDate: currentStart,
        endDate: Math.min(range.startDate - 1, requestedEnd),
      });
    }

    // Move current start to after this range
    if (range.endDate >= currentStart) {
      currentStart = range.endDate + 1;
    }

    // If we've covered the entire requested range, we're done
    if (currentStart > requestedEnd) {
      break;
    }
  }

  // If there's still a gap at the end, add it
  if (currentStart <= requestedEnd) {
    missing.push({
      startDate: currentStart,
      endDate: requestedEnd,
    });
  }

  return missing;
};

/**
 * Merge overlapping or adjacent date ranges
 */
function mergeRanges(ranges: DateRange[]): DateRange[] {
  if (ranges.length <= 1) {
    return ranges;
  }

  const sorted = ranges.sort((a, b) => a.startDate - b.startDate);
  const merged: DateRange[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = merged[merged.length - 1];

    // If current range overlaps or is adjacent to the last merged range
    if (current.startDate <= last.endDate + 1) {
      last.endDate = Math.max(last.endDate, current.endDate);
    } else {
      merged.push(current);
    }
  }

  return merged;
}
